import db from "../../database";
import { getOAuthHeaders } from "../../constants/oAuthValidation";
import { v4 as uuidv4 } from "uuid";
import { trackSync } from "../../utils/healthReporter";
import { verifySyncCompleteness } from "../../utils/dataIntegrity";
import { getCountries, getFinancialAccounts, getStates, syncDocTypes, syncRFIDData, syncRFIDScanConfig, syncSalesRepData } from "../../services/generic";
import {
  syncAllProductCategories,
  syncProductCategories,
  syncPosSaleTypes,
  syncProductsUom,
  syncProducts,
  syncPricingRules,
  syncOfflineDta,
  syncPosConfigData,
  syncLoyalityData,
  syncGiftCardData,
  getApprovals,
  getExpenseCategory,
  getCompanionRules,
  getRevenueCategories,
  getCustomerConfig,
  syncRestaurantTables,
  getFBOrdersData,
  getLoyaltyInfo,
  syncPricingPolicies,
} from "../../services";
import dayjs from "dayjs";
import useSyncProgressStore from "../../stores/useSyncProgressStore";
import useAuthStore from "../../stores/useAuthStore";
import useReferenceStore from "../../stores/useReferenceStore";
import useSessionStore from "../../stores/useSessionStore";
import { trace } from "../../utils/debugTrace";
import { isServerReachable } from "../../utils/serverReachability";

let cleanToken;
let syncId;
let tillId;
const authHeaders = getOAuthHeaders();
if (authHeaders) {
  cleanToken = authHeaders.access_token;
}

export const processSync = async (data, type) => {
  // Offline: every caller here (17+ across mount effects, sidebars, buttons,
  // login) funnels through this one function — resolve immediately and use
  // whatever's already cached locally instead of attempting network calls
  // that can't succeed. POS already supports offline mode; sync is a
  // background refinement of local data, never a gate on using the app.
  // Resolving (not throwing) means no caller needs a change — the app just
  // proceeds with local data exactly as if this sync round was skipped,
  // silently, with no error toasts for what is normal offline behavior.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    // navigator.onLine is a heuristic and can false-negative right after app
    // launch/login (seen on cold Electron/browser network-stack init). Trusting
    // it blindly here silently skipped the very first login sync, leaving
    // db.products permanently empty until the user noticed and ran a manual
    // Full Sync from the side menu. Confirm with an actual network probe
    // before giving up — see src/utils/serverReachability.js.
    const reachable = await isServerReachable();
    if (!reachable) {
      console.warn("[Sync] Offline — skipping sync, using local data");
      trace("BootDiag", "processSync skipped (offline)", { requestedType: type });
      return;
    }
    trace("BootDiag", "processSync: navigator.onLine false-negative, server reachable — proceeding", { requestedType: type });
  }

  const syncStart = performance.now();
  trace("BootDiag", "processSync start", { requestedType: type });
  // Reset the module-level syncId before any code that can throw runs. The
  // catch block below uses `if (syncId)` to decide whether to mark a
  // dataSyncSummary row FAILED — deletePreviousData() (moved inside the try
  // below) is now the first failable step, and it runs before syncId is
  // reassigned. Without this reset, a failure there would fall through to the
  // catch block still holding the *previous* call's syncId and incorrectly
  // mark that unrelated, already-completed sync record as FAILED.
  syncId = null;
  const setProgress = useSyncProgressStore.getState().setProgress;
  const tillData = data ? data : useAuthStore.getState().tillData;
  let syncType = "Incremental Sync";
  const lastUpdatedTime = useSessionStore.getState().lastUpdatedTime;
  let isFullSync = lastUpdatedTime === undefined || lastUpdatedTime === null;
  if (!isFullSync) {
    // lastUpdatedTime is persisted via sqliteImmediateStorage (the Electron
    // SQLite-backed store), which deliberately survives a browser-side
    // localStorage/IndexedDB clear (see useAuthStore.js's tillData/tillValue
    // comments for the same reasoning). If a user/dev clears IndexedDB —
    // wiping db.products — without clearing that SQLite store, lastUpdatedTime
    // still looks valid, so this would run as an Incremental Sync (deltas
    // only) against an empty catalog and never repopulate it. Self-heal by
    // forcing a Full Sync whenever the local product table is actually empty,
    // regardless of what lastUpdatedTime claims.
    const existingProductsCount = await db.products.count().catch(() => 0);
    if (existingProductsCount === 0) {
      isFullSync = true;
      trace("BootDiag", "forcing Full Sync — db.products empty despite lastUpdatedTime set", { lastUpdatedTime });
    }
  }
  if (isFullSync) {
    syncType = "Full Sync";
    // BUG-DIAG (products popup shows "No products"): db.products.clear() below
    // empties the table synchronously, but syncProducts() a few lines down
    // refills it across many awaited paginated batches (can take 10s+ on large
    // catalogs). This flag lets any direct db.products reader (e.g. the
    // grouped-item popup) know the table is mid-rebuild instead of silently
    // seeing zero rows — see src/utils/waitForProductsSync.js.
    useSyncProgressStore.getState().setProgress({ productsTableSyncing: true });
    trace("ProductsSync", "products table clearing for full sync", { requestedType: type });
  }
  try {
    if (isFullSync) {
      await deletePreviousData();
    }
    let startTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
    // tillId used to come solely from useAuthStore's tillValue.cwr_till_id — a
    // separate piece of state that several Till.js flows reset to setTillValue({})
    // (error paths, re-registration, auto-relogin) independently of tillData. When
    // that reset lands between till selection and this first post-login sync,
    // tillId is undefined here and every tillId-scoped query (categories, products)
    // silently matches nothing (no error — see BUG-1612's note on this same class
    // of bug for Sales History/eBill), while syncPosSaleTypes still succeeds because
    // it filters on tillData.tillAccess.csBunit.cwrPcatalogueId instead of tillId.
    // tillData.tillAccess.cwrTill.cwrTillID is the same identifier but always
    // populated whenever tillData itself is usable, so prefer it and only fall back
    // to the store value if tillData is somehow missing it.
    tillId = tillData?.tillAccess?.cwrTill?.cwrTillID || useAuthStore.getState().tillValue?.cwr_till_id;
    syncId = uuidv4().replace(/-/g, "").toUpperCase();
    await db.dataSyncSummary.add({ syncId: syncId, syncType: syncType, syncStartTime: startTime, syncStatus: "IN_PROGRESS" }); // DI-15: Track sync status
    setProgress({ percent: "15%" });
    setProgress({ productCategoryPercentage: "38" });
    await syncAllProductCategories(tillId, syncId);
    setProgress({ percent: "30%" });
    setProgress({ productCategoryPercentage: "55" });
    setProgress({ productPercentage: "15" });
    await syncProductCategories(tillData, tillId, syncId);
    setProgress({ percent: "45%" });
    setProgress({ productCategoryPercentage: "70" });
    setProgress({ productPercentage: "30" });
    await syncPosSaleTypes(syncId, tillId, tillData);
    setProgress({ percent: "53%" });
    setProgress({ productCategoryPercentage: "100" });
    setProgress({ productPercentage: "47" });
    await syncProductsUom(syncId, tillId);
    setProgress({ percent: "64%" });
    setProgress({ productPercentage: "56" });
    setProgress({ percent: "75%" });
    setProgress({ productPercentage: "69" });
    await syncProducts(tillData, syncId, tillId);
    if (isFullSync) {
      useSyncProgressStore.getState().setProgress({ productsTableSyncing: false });
      const productsCount = await db.products.count();
      trace("ProductsSync", "products table restored after full sync", { productsCount, elapsedMs: Math.round(performance.now() - syncStart) });
    }
    setProgress({ percent: "84%" });
    setProgress({ promotionPercentage: "25" });
    setProgress({ productPercentage: "77" });
    await syncPricingRules(tillData, syncId, tillId);
    await syncOfflineDta(tillData, syncId, tillId);
    setProgress({ percent: "90%" });
    setProgress({ promotionPercentage: "55" });
    setProgress({ productPercentage: "85" });
    await syncPosConfigData(syncId, tillId);
    trace("BootDiag", "processSync posConfig synced", { syncType, elapsedMs: Math.round(performance.now() - syncStart) });
    setProgress({ percent: "95%" });
    setProgress({ productPercentage: "100" });
    setProgress({ promotionPercentage: "79" });
    // Non-critical sync calls — each wrapped individually so one failure
    // doesn't block login. POS works without these; they'll sync next time.
    const nonCritical = async (name, fn) => {
      try {
        await fn();
      } catch (e) {
        console.warn(`[Sync] ${name} failed (non-blocking):`, e?.message);
      }
    };
    await nonCritical("LoyaltyData", () => syncLoyalityData(syncId, tillId));
    await nonCritical("GiftCardData", () => syncGiftCardData(syncId, tillId));
    await nonCritical("Approvals", () => getApprovals());
    await nonCritical("ExpenseCategory", () => getExpenseCategory(syncId));
    await nonCritical("RevenueCategories", () => getRevenueCategories(tillData.tillAccess.csBunit.csBunitId));
    nonCritical("CompanionRules", () => getCompanionRules());
    const bunitId = tillData.tillAccess.csBunit.csBunitId;
    const value = { bunitId };
    const newStringifiedFields = JSON.stringify(value).replace(/\\"/g, '\\"');
    const newStringRequest = JSON.stringify(newStringifiedFields);
    const apiBuilder = {
      query: `mutation { executeAPIBuilder(apiBuilderId:"69aabb8c6c3dd15f04fe382d", params: ${newStringRequest})} `,
    };
    await nonCritical("FinancialAccounts", () => getFinancialAccounts(apiBuilder));
    await nonCritical("CustomerConfig", () => getCustomerConfig());
    await nonCritical("DocTypes", () => syncDocTypes());
    nonCritical("RestaurantTables", () => syncRestaurantTables());
    await nonCritical("RFIDData", () => syncRFIDData(syncId));
    await nonCritical("RFIDScanConfig", () => syncRFIDScanConfig(tillData));
    await nonCritical("SalesRepData", () => syncSalesRepData(tillData));
    await nonCritical("FilterBrandCategory", () => filterBrandAndCategoty());
    await nonCritical("PricingPolicies", () => syncPricingPolicies(tillData, syncId, tillId));
    await nonCritical("FBOrdersData", () => getFBOrdersData());
    await nonCritical("States", () => getStates());
    await nonCritical("Countries", () => getCountries());
    const customerConfig = useReferenceStore.getState().customerConfigs;
    const customerConfigData = customerConfig ? JSON.parse(customerConfig[0]?.configuration) : {};
    const retailCustomerApiDataSources = customerConfigData?.apiDataSources || [];
    const loyaltyApiConfig = retailCustomerApiDataSources?.find((api) => api.name === "getLoyaltyData");
    if (loyaltyApiConfig && loyaltyApiConfig?.endpoint) {
      const params = {
        startRow: 0,
        endRow: 10,
      };
      try {
        await getLoyaltyInfo({
          endpoint: loyaltyApiConfig?.endpoint,
          params,
        });
      } catch (loyaltyErr) {
        console.warn("Loyalty info fetch failed (non-blocking):", loyaltyErr?.message);
      }
    }
    setProgress({ percent: "99%" });
    setProgress({ promotionPercentage: "100" });
    await db.dataSyncSummary
      .where("syncId")
      .equals(syncId)
      .modify((event) => {
        event.syncEndTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
        event.syncStatus = "COMPLETED"; // DI-15: Mark sync as completed
      });
    useSessionStore.getState().setLastUpdatedTime(startTime);
    trackSync(true);

    // DI-15 / DI-19: Verify sync completeness — warn if critical tables are empty
    try {
      const syncVerification = await verifySyncCompleteness(db);
      if (!syncVerification.complete) {
        console.warn("[DI-19] Sync completed but data verification failed:", syncVerification.warnings);
        // Store the verification result so the app can show a warning
        await db.dataSyncSummary
          .where("syncId")
          .equals(syncId)
          .modify((event) => {
            event.verificationWarnings = syncVerification.warnings;
            event.syncStatus = "INCOMPLETE";
          });
      } else {
      }
    } catch (verifyErr) {
      console.warn("[DI-19] Sync verification check failed (non-blocking):", verifyErr?.message);
    }

    // Push context to background scheduler (Electron only)
    if (window.electronAPI?.scheduler) {
      const tillDataLocal = useAuthStore.getState().tillData || {};
      const tillValueLocal = useAuthStore.getState().tillValue || {};
      const cookies = document.cookie.split("; ");
      let accessToken = "";
      for (const c of cookies) {
        const [name, ...rest] = c.split("=");
        if (name.includes("accessToken")) accessToken = rest.join("=");
      }
      const ctx = {
        bUnitId: tillDataLocal?.tillAccess?.csBunit?.csBunitId || "",
        tillId: tillValueLocal?.cwr_till_id || "",
        accessToken,
        graphqlUrl: window.__CW_TENANT_URLS__?.REACT_APP_serverUrl || "",
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      for (const [k, v] of Object.entries(ctx)) {
        window.electronAPI.scheduler.setContext(k, v);
      }
    }
    trace("BootDiag", "processSync complete", { syncType, elapsedMs: Math.round(performance.now() - syncStart) });
  } catch (error) {
    // Never leave productsTableSyncing stuck true on failure — that would
    // permanently block every popup/listing that waits on it (see
    // src/utils/waitForProductsSync.js).
    useSyncProgressStore.getState().setProgress({ productsTableSyncing: false });
    trace("BootDiag", "processSync failed", { syncType, elapsedMs: Math.round(performance.now() - syncStart), error: error?.message });
    trackSync(false);
    // DI-15: Mark sync as failed so the app can detect partial data on next startup
    try {
      if (syncId) {
        await db.dataSyncSummary
          .where("syncId")
          .equals(syncId)
          .modify((event) => {
            event.syncStatus = "FAILED";
            event.syncError = error?.message || String(error);
          });
      }
    } catch (markErr) {
      console.warn("[DI-15] Failed to mark sync as FAILED:", markErr?.message);
    }
    throw error;
  }
};

export const deletePreviousData = () => {
  return new Promise(async (deletionSuccess, deletionFailure) => {
    try {
      await db.productCategories.clear();
      await db.AllProductCategories.clear();
      await db.products.clear();
      await db.pricingRules.clear();
      await db.posSaletypes.clear();
      await db.productUom.clear();
      await db.rfidData.clear();
      await db.loyalityData.clear();
      await db.POSWorkFlowRules.clear();
      await db.approvers.clear();
      await db.productBrands.clear();
      await db.giftCardData.clear();
      await db.docTypesData.clear();
      useSessionStore.getState().setLastUpdatedTime(null);
      deletionSuccess();
    } catch (error) {
      deletionFailure(error);
    }
  });
};

const filterBrandAndCategoty = async () => {
  // `isactive` cannot be queried via where().equals() cross-adapter: SQLite coerces it to integer 1,
  // but Dexie/IndexedDB stores it as boolean `true` and rejects booleans as index keys (DataError:
  // "not a valid key"). Filter in JS with loose `== true` — the same approach both search-index
  // builders use — so it matches whether isactive is stored as 1 (SQLite) or true (Dexie).
  let products = await db.products.filter((product) => product.isactive == true).toArray();
  let brandDetails = await db.productBrands.toArray();
  let categoryDetails = await db.productCategories.toArray();
  // Fall back to AllProductCategories if productCategories is empty (some tenants' productCategory API returns no data)
  if (categoryDetails.length === 0) {
    categoryDetails = await db.AllProductCategories.toArray();
  }
  await db.productBrands.clear();
  await db.productCategories.clear();
  const getUniqueBrandsAndCategories = (products) => {
    const brands = new Set();
    const categories = new Set();

    products.forEach((product) => {
      brands.add(product.brandId);
      categories.add(product.mProductCategoryId);
    });

    return {
      brands: Array.from(brands),
      categories: Array.from(categories),
    };
  };
  const { brands, categories } = getUniqueBrandsAndCategories(products);

  const filterBrandsAndCategories = (brands, categories, brandDetails, categoryDetails) => {
    const filteredBrands = brands.reduce((acc, brandId) => {
      const brandDetail = brandDetails.find((brand) => brand.brandId === brandId);
      if (brandDetail) {
        acc.push(brandDetail);
      }
      return acc;
    }, []);

    const filteredCategories = categories.reduce((acc, categoryId) => {
      const categoryDetail = categoryDetails.find((category) => category.mProductCategoryId === categoryId);
      if (categoryDetail) {
        acc.push(categoryDetail);
      }
      return acc;
    }, []);

    return {
      filteredBrands,
      filteredCategories,
    };
  };

  const { filteredBrands, filteredCategories } = filterBrandsAndCategories(brands, categories, brandDetails, categoryDetails);
  await db.productBrands.bulkPut(filteredBrands);
  await db.productCategories.bulkPut(filteredCategories);
};
